import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import BucketChip from '../components/BucketChip';
import NumLink from '../components/NumLink';
import CommercialCells from '../components/CommercialCells';
import { EMPTY, PANEL, TD, TH } from '../components/tableStyles';
import { num, pct, titleCase } from '../format';
import { totals, type Totals } from '../stats';
import { BUCKETS, WORK_BUCKETS } from '../theme';
import type { OpenDrill, WorkRow } from '../types';

interface Props { rows: WorkRow[]; openDrill: OpenDrill }

type SortKey = 'agent' | 'workItems' | 'worked' | 'unworked' | 'workRate' | 'uniqueCustomers' | 'orders' | 'convPct'
  | 'booked' | 'delivered' | 'inTransit' | 'awaitingPickup' | 'returned' | 'cancelled' | 'deliveryPct' | 'inTransitPct' | 'rtoPct' | 'aov';

/** Every column, left to right. Commercial columns follow the Agent Dashboard's order and stage rule. */
const COLUMNS: { key: SortKey; label: string; value: (t: Totals) => number | null }[] = [
  { key: 'workItems', label: 'Work items', value: t => t.workItems },
  { key: 'worked', label: 'Worked', value: t => t.worked },
  { key: 'unworked', label: 'Unworked', value: t => t.unworked },
  { key: 'workRate', label: 'Worked %', value: t => t.workRate },
  { key: 'uniqueCustomers', label: 'Unique customers', value: t => t.uniqueCustomers },
  { key: 'orders', label: 'Orders', value: t => t.orders },
  { key: 'convPct', label: 'Conv%', value: t => t.convPct },
  { key: 'booked', label: 'Booked', value: t => t.booked },
  { key: 'delivered', label: 'Delivered', value: t => t.stages.delivered.value },
  { key: 'inTransit', label: 'In Transit', value: t => t.stages.inTransit.value },
  { key: 'awaitingPickup', label: 'Awaiting Pickup', value: t => t.stages.awaitingPickup.value },
  { key: 'returned', label: 'Returned', value: t => t.stages.returned.value },
  { key: 'cancelled', label: 'Cancelled', value: t => t.stages.cancelled.value },
  { key: 'deliveryPct', label: 'Delivery %', value: t => t.deliveryPct },
  { key: 'inTransitPct', label: 'In Transit %', value: t => t.inTransitPct },
  { key: 'rtoPct', label: 'RTO %', value: t => t.rtoPct },
  { key: 'aov', label: 'AOV', value: t => t.bookedAov },
];

/** The five consolidated work cells for an agent row or the grand total (Unique customers follows, as its own cell). */
const WorkCells: React.FC<{ t: Totals }> = ({ t }) => {
  const c = `${TD} text-right tabular-nums`;
  return (
    <>
      <td className={c}>{num(t.workItems)}</td>
      <td className={c}>{num(t.worked)}</td>
      <td className={c}>{num(t.unworked)}</td>
      <td className={`${TD} text-right tabular-nums`}>{pct(t.workRate)}</td>
    </>
  );
};

/** Agent x bucket: work volume, worked and commercial result side by side, never blended. */
const AgentBucketTab: React.FC<Props> = ({ rows, openDrill }) => {
  const agents = useMemo(() => Array.from(new Set(rows.map(r => r.agent))).sort((a, b) => (a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b))), [rows]);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const all = useMemo(() => totals(rows), [rows]);

  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'agent', dir: 'asc' });
  const col = COLUMNS.find(c => c.key === sort.key);
  const sortBy = (key: SortKey) => setSort(p => (p.key === key
    ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: key === 'agent' ? 'asc' : 'desc' }));
  /** Numbers high-to-low on first click; a blank (—) always sinks to the bottom, whichever way it is sorted. */
  const compare = (a: number | null, b: number | null) => {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return sort.dir === 'asc' ? a - b : b - a;
  };
  const sortedAgents = useMemo(() => {
    if (!col) return agents;                                   // 'agent': the name order above
    const by = new Map(agents.map(a => [a, col.value(totals(rows.filter(r => r.agent === a)))]));
    return [...agents].sort((x, y) => compare(by.get(x) ?? null, by.get(y) ?? null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, rows, sort]);
  const sortedBuckets = (mine: WorkRow[]) => (col ? [...mine].sort((x, y) => compare(col.value(totals([x])), col.value(totals([y])))) : mine);
  const arrow = (key: SortKey) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  if (agents.length === 0) return <div className={EMPTY}>No agent work for this selection.</div>;

  const toggle = (a: string) => setOpen(prev => { const n = new Set(prev); if (n.has(a)) n.delete(a); else n.add(a); return n; });

  return (
    <div className={PANEL}>
      <div className="flex items-center justify-end border-b border-slate-100 px-4 py-3 dark:border-slate-700">
        <div className="flex gap-2 text-sm">
          <button type="button" onClick={() => setOpen(new Set(agents))} className="rounded-lg px-2.5 py-1 text-violet-700 hover:bg-violet-50 dark:text-violet-300 dark:hover:bg-violet-400/10">Expand all</button>
          <button type="button" onClick={() => setOpen(new Set())} className="rounded-lg px-2.5 py-1 text-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">Collapse all</button>
        </div>
      </div>
      <div className="max-h-[70vh] overflow-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-brandColor">
              <th onClick={() => sortBy('agent')} className={`${TH} sticky left-0 top-0 z-30 cursor-pointer select-none bg-brandColor !text-white text-left whitespace-nowrap`}>Agent / Bucket{arrow('agent')}</th>
              {COLUMNS.map(c => (
                <th key={c.key} onClick={() => sortBy(c.key)} className={`${TH} sticky top-0 z-20 cursor-pointer select-none bg-brandColor !text-white text-right whitespace-nowrap`}>{c.label}{arrow(c.key)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedAgents.map(agent => {
              const mine = rows.filter(r => r.agent === agent);
              const t = totals(mine);
              const isOpen = open.has(agent);
              return (
                <React.Fragment key={agent}>
                  <tr className="cursor-pointer border-t border-slate-100 bg-slate-50/60 font-semibold hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60"
                    onClick={() => toggle(agent)}>
                    <td className={`${TD} sticky left-0 z-10 whitespace-nowrap bg-slate-50 dark:bg-slate-800`}>
                      <span className="flex items-center gap-1.5 whitespace-nowrap">
                        {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-600" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-600" />}
                        {titleCase(agent)}
                        <span className="ml-1 flex gap-0.5">
                          {WORK_BUCKETS.filter(b => mine.some(r => r.bucket === b)).map(b => <span key={b} className={`h-2 w-2 rounded-full ${BUCKETS[b].dot}`} title={BUCKETS[b].label} />)}
                        </span>
                      </span>
                    </td>
                    <WorkCells t={t} />
                    <td className={`${TD} text-right tabular-nums`}>{num(t.uniqueCustomers)}</td>
                    <CommercialCells t={t} drill={(status, what, total) =>
                      openDrill({ title: `${titleCase(agent)} — ${what}`, kind: 'orders', agent, bucket: 'all', total, orderStatus: status ?? 'all' })} />
                  </tr>
                  {isOpen && sortedBuckets(mine).map(r => (
                    <tr key={`${agent}-${r.bucket}`} className={`border-t border-slate-50 dark:border-slate-700/50 ${BUCKETS[r.bucket].tint}`}>
                      <td className={`${TD} sticky left-0 z-10 whitespace-nowrap bg-white pl-9 dark:bg-slate-800`}><BucketChip bucket={r.bucket} /></td>
                      <td className={`${TD} text-right`}><NumLink text={num(r.workItems)} zero={r.workItems === 0} onClick={() => openDrill({ title: `${agent} · ${BUCKETS[r.bucket].label} — work items`, subtitle: BUCKETS[r.bucket].unit, kind: 'work', agent, bucket: r.bucket, total: r.workItems })} /></td>
                      <td className={`${TD} text-right`}><NumLink text={num(r.worked)} zero={r.worked === 0} onClick={() => openDrill({ title: `${agent} · ${BUCKETS[r.bucket].label} — worked`, kind: 'work', agent, bucket: r.bucket, total: r.worked, worked: 'worked' })} /></td>
                      <td className={`${TD} text-right`}><NumLink text={num(r.received - r.worked)} zero={r.received - r.worked === 0} onClick={() => openDrill({ title: `${agent} · ${BUCKETS[r.bucket].label} — unworked`, kind: 'work', agent, bucket: r.bucket, total: r.received - r.worked, worked: 'unworked' })} /></td>
                      <td className={`${TD} text-right tabular-nums`}>{pct(totals([r]).workRate)}</td>
                      <td className={`${TD} text-right tabular-nums`}>{num(r.uniqueCustomers)}</td>
                      <CommercialCells t={totals([r])} drill={(status, what, total) =>
                        openDrill({ title: `${titleCase(agent)} · ${BUCKETS[r.bucket].label} — ${what}`, kind: 'orders', agent, bucket: r.bucket, total, orderStatus: status ?? 'all' })} />
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="border-t border-slate-200 bg-slate-50/80 font-semibold dark:border-slate-600 dark:bg-slate-800">
            <tr>
              <td className={`${TD} sticky left-0 z-10 bg-slate-50 dark:bg-slate-800`}>All agents</td>
              <WorkCells t={all} />
              <td className={`${TD} text-right tabular-nums`}>{num(all.uniqueCustomers)}</td>
              <CommercialCells t={all} drill={(status, what, total) =>
                openDrill({ title: `All agents — ${what}`, kind: 'orders', agent: 'all', bucket: 'all', total, orderStatus: status ?? 'all' })} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default AgentBucketTab;
